import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import { Button } from "@mapmycustomers/ui";

import MultiSelectActivityTypeField from "@app/component/input/MultiSelectActivityTypeField";
import defaultFilterOption from "@app/component/input/utils/defaultFilterOption";
import { smartPlannerViewAnalytics } from "@app/scene/activity/activityTableAnalytics";
import { getActivityTypes } from "@app/store/activity";
import { RootState } from "@app/store/rootReducer";

import DateRangeType from "../../enum/DateRangeType";
import {
  areActivitiesLoading,
  getActivities,
  getRange,
  getRangeType,
  getSelectedActivityTypes,
} from "../../store";
import { setActivityTypes } from "../../store/actions";
import StepProps from "../../type/StepProps";
import Footer from "../Footer";
import Counter, { messages } from "../Footer/Counter";
import Step from "../Step";

import DateRangeSelector from "./DateRangeSelector";
import EntityTypeSelector from "./EntityTypeSelector";
import styles from "./Setup.module.scss";

interface Props extends StepProps {
  activitiesCount: number;
  activityTypes: ActivityType[];
  isValid: boolean;
  loading?: boolean;
  onChangeActivityTypes: (activityTypes: ActivityType[]) => void;
  selectedActivityTypes: ActivityType[];
}

const Setup: React.FC<Props> = ({
  activitiesCount,
  activityTypes,
  isValid,
  loading,
  onChangeActivityTypes,
  onNext,
  selectedActivityTypes,
}) => {
  const intl = useIntl();

  useEffect(() => {
    smartPlannerViewAnalytics?.using(["Build routes from activities", "Open step"], {
      stepName: "Set up route",
    });
  }, []);

  const handleChangeActivityTypes = useCallback(
    (activityTypesIds: ActivityType["id"][]) => {
      onChangeActivityTypes(activityTypes.filter(({ id }) => activityTypesIds.includes(id)));
    },
    [activityTypes, onChangeActivityTypes]
  );

  return (
    <Step
      contentClassName={styles.container}
      footer={
        <Footer
          counter={
            <Counter
              comment={isValid ? "" : intl.formatMessage(messages.setupDisabled)}
              count={activitiesCount}
              loading={loading}
              showCount={false}
            />
          }
        >
          <Button disabled={!isValid} onClick={onNext} type="primary">
            {intl.formatMessage({
              id: "activities.calendarView.routeBuilder.steps.setup.footer.next",
              defaultMessage: "Next step",
              description: "Next button in Setup step in Route Builder in Activities Calendar view",
            })}
          </Button>
        </Footer>
      }
    >
      <div className={styles.warning}>
        {intl.formatMessage(
          {
            id: "activities.calendarView.routeBuilder.steps.setup.warning",
            defaultMessage:
              "Easily build a route based on activities you already have planned. Please note, the builder will only include activities <highlight>assigned to you</highlight>.",
            description: "Warning in Setup step in Route Builder in Activities Calendar view",
          },
          {
            highlight: (text) => <span className={styles.warningHighlight}>{text}</span>,
          }
        )}
      </div>
      <EntityTypeSelector />
      <DateRangeSelector />
      <MultiSelectActivityTypeField
        className={styles.label}
        filterOption={defaultFilterOption}
        label={intl.formatMessage({
          id: "activities.calendarView.routeBuilder.steps.setup.activityTypeSelector.title",
          defaultMessage: "Include activities of the following types",
          description:
            "Title of activity type selector in Route Builder in Activities Calendar view",
        })}
        onChange={handleChangeActivityTypes}
        value={selectedActivityTypes.map(({ id }) => id)}
      />
    </Step>
  );
};

const mapStateToProps = (state: RootState) => ({
  activitiesCount: getActivities(state).length,
  activityTypes: getActivityTypes(state),
  isValid:
    getSelectedActivityTypes(state).length > 0 &&
    (getRangeType(state) !== DateRangeType.CUSTOM || !!getRange(state)),
  loading: areActivitiesLoading(state),
  selectedActivityTypes: getSelectedActivityTypes(state),
});

const mapDispatchToProps = {
  onChangeActivityTypes: setActivityTypes,
};

export default connect(mapStateToProps, mapDispatchToProps)(Setup);
